import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw, Loader2, X } from "lucide-react";
import {
  resolvePWACacheConflicts,
  checkPWACacheConflicts
} from "@/utilities/pwa-utils";
import { cn } from "@/utilities/tailwind-utils";

export function PWACacheResolver() {
  const [isVisible, setIsVisible] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [conflicts, setConflicts] = useState<{
    hasConflicts: boolean;
    cacheNames: string[];
    details: string[];
  }>({
    hasConflicts: false,
    cacheNames: [],
    details: []
  });

  useEffect(() => {
    // Check for cache conflicts on mount
    const checkForConflicts = async () => {
      try {
        const result = await checkPWACacheConflicts();
        setConflicts(result);

        // Show the resolver if there are conflicts
        if (result.hasConflicts) {
          setIsVisible(true);
        }
      } catch (error) {
        console.error("Failed to check for cache conflicts:", error);
      }
    };
    checkForConflicts();
  }, []);

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      await resolvePWACacheConflicts();
      // The page will reload automatically
    } catch (error) {
      console.error("Failed to resolve cache conflicts:", error);
      setIsResolving(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  // Don't render if no conflicts
  if (!conflicts.hasConflicts && !isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-4 left-4 z-50 transition-all duration-300 ease-in-out",
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      )}
    >
      <Card
        className={cn(
          "relative overflow-hidden border-0 shadow-xl max-w-md",
          "bg-gradient-to-r from-red-50 to-orange-50",
          "dark:from-red-950/30 dark:to-orange-950/30",
          "backdrop-blur-sm border border-red-200/50 dark:border-red-800/50"
        )}
      >
        {/* Animated background indicator */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 animate-pulse" />

        <div className="relative p-4">
          <div className="flex items-start gap-3">
            {/* Status icon */}
            <div
              className={cn(
                "flex-shrink-0 w-10 h-10 rounded-full",
                "bg-red-100 dark:bg-red-900/50",
                "flex items-center justify-center"
              )}
            >
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-red-900 dark:text-red-100">
                  PWA Cache Conflict
                </h4>
                <Badge variant="destructive" className="text-xs">
                  Error
                </Badge>
              </div>

              <p className="text-sm text-red-700 dark:text-red-200/80 mb-3">
                Conflicting cache entries detected. This may cause the app to
                malfunction.
              </p>

              {/* Conflict details */}
              {conflicts.details.length > 0 && (
                <div className="mb-3">
                  <details className="text-xs">
                    <summary className="cursor-pointer text-red-600 dark:text-red-400 mb-1">
                      View Details ({conflicts.details.length})
                    </summary>
                    <div className="space-y-1">
                      {conflicts.details.map((detail, index) => (
                        <div
                          key={index}
                          className="text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/20 p-2 rounded"
                        >
                          {detail}
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleResolve}
                  disabled={isResolving}
                  className={cn(
                    "bg-red-600 hover:bg-red-700",
                    "text-white shadow-sm",
                    "transition-all duration-200",
                    "hover:shadow-md active:scale-95"
                  )}
                >
                  {isResolving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {isResolving ? "Resolving..." : "Resolve Conflict"}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  disabled={isResolving}
                  className={cn(
                    "flex-shrink-0 h-8 w-8 p-0 rounded-full",
                    "text-red-600 hover:text-red-700",
                    "hover:bg-red-100 dark:hover:bg-red-900/50",
                    "transition-colors duration-200"
                  )}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
